import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import type {
  AuthSession,
  LotDetail,
  LotSummary,
  Payment,
  Quote,
  Receipt,
  SessionView,
  Vehicle,
} from './types';

export const keys = {
  lots: (lat?: number, lon?: number) => ['lots', lat ?? null, lon ?? null] as const,
  lot: (id: string) => ['lot', id] as const,
  lotByCode: (code: string) => ['lot-by-code', code] as const,
  quote: (id: string) => ['quote', id] as const,
  vehicles: ['vehicles'] as const,
  sessions: ['sessions'] as const,
  session: (id: string) => ['session', id] as const,
  history: ['history'] as const,
  receipt: (id: string) => ['receipt', id] as const,
  payment: (id: string) => ['payment', id] as const,
  currentUser: ['auth', 'me'] as const,
};

export const useCurrentUser = (enabled: boolean) =>
  useQuery({
    queryKey: keys.currentUser,
    queryFn: ({ signal }) => api.get<AuthSession['user']>('/auth/me', signal),
    enabled,
    staleTime: 60_000,
    retry: false,
  });

export const useNearbyLots = (coords?: { latitude: number; longitude: number }) =>
  useQuery({
    queryKey: keys.lots(coords?.latitude, coords?.longitude),
    queryFn: ({ signal }) =>
      api.get<{ lots: LotSummary[] }>(
        `/lots${coords ? `?latitude=${coords.latitude}&longitude=${coords.longitude}` : ''}`,
        signal,
      ),
    staleTime: 60_000,
  });

export const useLot = (lotId: string | undefined) =>
  useQuery({
    queryKey: keys.lot(lotId ?? ''),
    queryFn: ({ signal }) => api.get<LotDetail>(`/lots/${lotId}`, signal),
    enabled: Boolean(lotId),
  });

export const useLotByCode = (code: string | undefined) =>
  useQuery({
    queryKey: keys.lotByCode(code ?? ''),
    queryFn: ({ signal }) => api.get<LotDetail>(`/lots/by-code/${code}`, signal),
    enabled: Boolean(code),
    retry: false,
  });

export const useVehicles = (enabled = true) =>
  useQuery({
    queryKey: keys.vehicles,
    queryFn: ({ signal }) => api.get<{ vehicles: Vehicle[] }>('/vehicles', signal),
    enabled,
  });

export function useAddVehicle() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { plate: string; state: string; label?: string }) =>
      api.post<Vehicle>('/vehicles', input),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.vehicles }),
  });
}

/**
 * State and label only. The plate NUMBER is immutable server-side (sessions
 * snapshot it and the promo lock window keys off it), so correcting a plate
 * number means delete-and-re-add. The body is strict: send only the fields
 * being changed.
 */
export function usePatchVehicle() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ vehicleId, ...patch }: { vehicleId: string; state?: string; label?: string | null }) =>
      api.patch<Vehicle>(`/vehicles/${vehicleId}`, patch),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.vehicles }),
  });
}

export function useSetDefaultVehicle() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (vehicleId: string) => api.post<Vehicle>(`/vehicles/${vehicleId}/default`),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.vehicles }),
  });
}

// Soft delete server-side: running and historical sessions keep their plate
// snapshot, so removing a plate never voids a paid stay.
export function useDeleteVehicle() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (vehicleId: string) => api.delete<void>(`/vehicles/${vehicleId}`),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.vehicles }),
  });
}

export const useStates = () =>
  useQuery({
    queryKey: ['states'],
    queryFn: ({ signal }) =>
      api.get<{ priority: string[]; all: string[]; default: string }>('/vehicles/states', signal),
    staleTime: Number.POSITIVE_INFINITY,
  });

export const useActiveSessions = (enabled = true) =>
  useQuery({
    queryKey: keys.sessions,
    queryFn: ({ signal }) => api.get<{ sessions: SessionView[] }>('/sessions', signal),
    enabled,
    refetchInterval: 60_000,
  });

export const useSession = (sessionId: string | undefined) =>
  useQuery({
    queryKey: keys.session(sessionId ?? ''),
    queryFn: ({ signal }) => api.get<SessionView>(`/sessions/${sessionId}`, signal),
    enabled: Boolean(sessionId),
    refetchInterval: 60_000,
  });

export const useHistory = () =>
  useQuery({
    queryKey: keys.history,
    queryFn: ({ signal }) => api.get<{ sessions: SessionView[] }>('/sessions/history', signal),
  });

export const useReceipt = (sessionId: string | undefined) =>
  useQuery({
    queryKey: keys.receipt(sessionId ?? ''),
    queryFn: ({ signal }) => api.get<Receipt>(`/sessions/${sessionId}/receipt`, signal),
    enabled: Boolean(sessionId),
  });

export const useCreateQuote = () =>
  useMutation({
    mutationFn: (input: {
      lotId: string;
      vehicleId: string;
      spaceNumber: number | null;
      durationMinutes: number;
    }) => api.post<{ quote: Quote; duplicateWarning: SessionView | null }>('/quotes', input),
  });

export const useQuote = (quoteId: string | undefined) =>
  useQuery({
    queryKey: keys.quote(quoteId ?? ''),
    queryFn: ({ signal }) =>
      api.get<{ quote: Quote; duplicateWarning: SessionView | null }>(`/quotes/${quoteId}`, signal),
    enabled: Boolean(quoteId),
    staleTime: 5_000,
  });

export const useInvalidateQuote = () =>
  useMutation({ mutationFn: (quoteId: string) => api.delete<void>(`/quotes/${quoteId}`) });

export const useCreateExtensionQuote = () =>
  useMutation({
    mutationFn: (input: { sessionId: string; additionalMinutes: number }) =>
      api.post<Quote>(`/sessions/${input.sessionId}/extension-quote`, {
        additionalMinutes: input.additionalMinutes,
      }),
  });

/** The payment as the server recorded it — the only source for the tender split. */
export const usePayment = (paymentId: string | undefined) =>
  useQuery({
    queryKey: keys.payment(paymentId ?? ''),
    queryFn: ({ signal }) => api.get<Payment>(`/payments/${paymentId}`, signal),
    enabled: Boolean(paymentId),
  });

/** Creates (or re-fetches, if the key repeats) the PaymentIntent for a quote. */
export const useStartPayment = () =>
  useMutation({
    mutationFn: (input: { quoteId: string; idempotencyKey: string }) =>
      api.post<{ payment: Payment; publishableKey: string | null }>(
        '/payments',
        { quoteId: input.quoteId },
        { 'idempotency-key': input.idempotencyKey },
      ),
  });

/** Asks the server to reconcile with Stripe. Safe to call repeatedly. */
export function useSettlePayment() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (paymentId: string) => api.post<Payment>(`/payments/${paymentId}/settle`),
    onSuccess: (payment) => {
      if (payment.sessionId) {
        client.removeQueries({ queryKey: keys.session(payment.sessionId), exact: true });
        client.removeQueries({ queryKey: keys.receipt(payment.sessionId), exact: true });
      }
      void client.invalidateQueries({ queryKey: keys.sessions });
      void client.invalidateQueries({ queryKey: keys.history });
    },
  });
}
